import { Range } from "@tiptap/core";
import { Image } from "../Image";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    imageBlock: {
      setImageBlock: (attributes: { src: string }) => ReturnType;
      setImageBlockAt: (attributes: {
        src: string;
        pos: number | Range;
      }) => ReturnType;
      setImageBlockAlign: (align: "left" | "center" | "right") => ReturnType;
      setImageBlockWidth: (width: number) => ReturnType;
    };
  }
}

export const ImageBlock = Image.extend({
  name: "imageBlock",

  group: "block",

  defining: true,

  isolating: true,

  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (element) => element.getAttribute("src"),
        renderHTML: (attributes) => ({
          src: attributes.src,
        }),
      },
      width: {
        default: "100%",
        parseHTML: (element) => element.getAttribute("data-width"),
        renderHTML: (attributes) => ({
          "data-width": attributes.width,
        }),
      },
      align: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align"),
        renderHTML: (attributes) => ({
          "data-align": attributes.align,
        }),
      },
      alt: {
        default: undefined,
        parseHTML: (element) => element.getAttribute("alt"),
        renderHTML: (attributes) => ({
          alt: attributes.alt,
        }),
      },
      link: {
        default: undefined,
        parseHTML: (element) => element.getAttribute("data-link"),
        renderHTML: (attributes) => ({
          "data-link": attributes.link,
        }),
      },
      target: {
        default: undefined,
        parseHTML: (element) => element.getAttribute("data-target"),
        renderHTML: (attributes) => ({
          "data-target": attributes.target,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src*="tiptap.dev"]:not([src^="data:"]), img[src*="windows.net"]:not([src^="data:"])',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const hasLink = HTMLAttributes["data-link"];
    const alignOptions = {
      center: "center",
      left: "left",
      right: "right",
    };
    const align =
      alignOptions[HTMLAttributes["data-align"] as keyof typeof alignOptions] ??
      "center";
    const imageStyles = {
      width: HTMLAttributes["data-width"] ?? "100%",
      borderRadius: "10px",
    };

    if (hasLink) {
      return [
        "div",
        {
          style: {
            textAlign: align,
            width: "100%",
          },
        },
        [
          "a",
          {
            href: HTMLAttributes["data-link"],
            target: HTMLAttributes["data-target"],
            style: {
              display: "inline-block",
            },
          },
          [
            "img",
            {
              ...HTMLAttributes,
              style: {
                ...imageStyles,
              },
            },
          ],
        ],
      ];
    } else {
      return [
        "div",
        {
          style: {
            textAlign: align,
            width: "100%",
          },
        },
        [
          "img",
          {
            ...HTMLAttributes,
            style: {
              ...imageStyles,
              marginTop: "10px",
              marginBottom: "10px",
            },
          },
        ],
      ];
    }
  },
  addCommands() {
    return {
      setImageBlock:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: "imageBlock",
            attrs: { src: attrs.src },
          });
        },

      setImageBlockAt:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContentAt(attrs.pos, {
            type: "imageBlock",
            attrs: { src: attrs.src },
          });
        },

      setImageBlockAlign:
        (align) =>
        ({ commands }) =>
          commands.updateAttributes("imageBlock", { align }),

      setImageBlockWidth:
        (width) =>
        ({ commands }) =>
          commands.updateAttributes("imageBlock", {
            width: `${Math.max(0, Math.min(100, width))}%`,
          }),
    };
  },
});

export default ImageBlock;
